/**
 * @module toElement
 *
 * @see https://0.vuetifyjs.com/composables/transformers/to-element
 *
 * @remarks
 * Resolves various element reference types to a DOM Element.
 *
 * Accepts refs, getters, raw DOM elements, or Vue component instances
 * and normalizes them to a plain Element. Uses structural typing to
 * avoid cross-version Vue Ref incompatibilities.
 *
 * @example
 * ```ts
 * import { shallowRef } from 'vue'
 * import { toElement } from '@vuetify/v0'
 *
 * const el = shallowRef<HTMLElement | null>(null)
 * const resolved = toElement(el) // HTMLElement | undefined
 * ```
 */

// Utilities
import { isElement, isFunction, isNullOrUndefined, isObject } from '#v0/utilities'

// Types
import type { ComponentPublicInstance, VNode } from 'vue'

type MaybeElementValue = HTMLElement | SVGElement | ComponentPublicInstance | Element | null | undefined

/**
 * A flexible element ref type that accepts refs, getters, or raw values
 * containing DOM elements or Vue component instances.
 *
 * @remarks
 * Defined structurally (using `{ readonly value: T }`) instead of Vue's
 * nominal `Ref<T>` to avoid type mismatches when consumers use a
 * different Vue version than the library.
 * Use {@link toElement} to resolve the actual DOM element.
 */
export type MaybeElementRef =
  | { readonly value: MaybeElementValue }
  | (() => MaybeElementValue)
  | MaybeElementValue

/**
 * Resolves a component instance to its first root Element.
 *
 * A single-element root resolves to `$el`. A fragment root (multiple root
 * nodes, or leading text/comments) has a text anchor as `$el`, so the first
 * Element between the fragment's start and end anchors is returned. The walk
 * is bounded by the end anchor so it never escapes into a sibling. A comment
 * (`v-if` false) or text root resolves to undefined.
 */
function fromInstance (instance: ComponentPublicInstance): Element | undefined {
  const el: unknown = instance.$el
  if (isElement(el)) return el

  // Descend through component roots to the vnode that owns the DOM range
  let tree: VNode | null | undefined = instance.$?.subTree
  while (!isNullOrUndefined(tree?.component)) tree = tree.component.subTree

  const end = tree?.anchor as Node | null | undefined
  if (isNullOrUndefined(end)) return undefined

  let node = (tree?.el as Node | null | undefined)?.nextSibling
  while (!isNullOrUndefined(node) && node !== end) {
    if (isElement(node)) return node
    node = node.nextSibling
  }

  return undefined
}

/**
 * Resolves a {@link MaybeElementRef} to a DOM Element or undefined.
 *
 * Handles:
 * - `Ref<Element>`, `ShallowRef<HTMLElement>`, getter functions
 * - Vue component instances (`$el`, or the first root Element of a fragment root)
 * - Raw DOM elements (pass-through)
 * - null/undefined (returns undefined)
 *
 * @param target The element ref, getter, or raw value to resolve
 * @returns The resolved DOM Element, or undefined if not available
 *
 * @see https://0.vuetifyjs.com/composables/transformers/to-element
 *
 * @example
 * ```ts
 * import { toElement } from '@vuetify/v0'
 *
 * const el = shallowRef<HTMLElement | null>(null)
 * toElement(el) // HTMLElement | undefined
 *
 * const component = ref<ComponentPublicInstance>()
 * toElement(component) // Element | undefined (first root Element)
 * ```
 */
/* #__NO_SIDE_EFFECTS__ */
export function toElement (target: MaybeElementRef): Element | undefined {
  if (!target) return undefined
  // Raw element passes through first — Element shapes with their own `value`
  // property (e.g. <li>, <button>, <input>) would otherwise be misread as a
  // ref-like { value } and have their attribute value extracted instead.
  if (isElement(target)) return target
  const raw = isFunction(target)
    ? target()
    : (isObject(target) && 'value' in target
        ? target.value
        : target)
  if (!raw) return undefined
  if (isElement(raw)) return raw
  if (isObject(raw) && '$el' in raw) return fromInstance(raw as ComponentPublicInstance)
  return undefined
}
